import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer, CheckCircle, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PublicPatientLogReport() {
  const [searchParams] = useSearchParams();
  const [hasAutoPrinted, setHasAutoPrinted] = useState(false);

  const patientId = searchParams.get("id") || "";
  const patientName = searchParams.get("name") || "Patient";
  const dob = searchParams.get("dob") || "N/A";
  const gender = searchParams.get("gender") || "Not specified";
  const mobile = searchParams.get("mobile") || "N/A";
  const email = searchParams.get("email") || "Not provided";
  const registrationDate = searchParams.get("regDate") || new Date().toLocaleString("en-US");
  const acknowledgementDate = searchParams.get("ackDate") || registrationDate;
  const location = searchParams.get("loc") || "In-Person / Pelham Branch";
  const primaryProvider = searchParams.get("primary") || "Not assigned";
  const secondaryProvider = searchParams.get("secondary") || "";
  const autoPrint = searchParams.get("autoPrint") === "true";

  useEffect(() => {
    document.title = `Patient Enrollment Report - ${patientName}`;
    if (autoPrint && !hasAutoPrinted) {
      setHasAutoPrinted(true);
      const timer = setTimeout(() => {
        window.print();
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [autoPrint, hasAutoPrinted, patientName]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans print:bg-white print:p-0">
      {/* Non-printing control header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-4 shadow-sm print:hidden">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-extrabold text-lg text-emerald-800 tracking-tight">
              MonitorHealth<span className="text-emerald-500">.AI</span>
            </span>
            <span className="text-xs bg-emerald-100 text-emerald-800 font-semibold px-2.5 py-0.5 rounded-full border border-emerald-200">
              Official Patient Enrollment Report
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handlePrint}
              className="bg-emerald-700 hover:bg-emerald-800 text-white font-medium shadow-sm flex items-center gap-2 px-5"
            >
              <Printer className="w-4 h-4" />
              Download / Print PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => window.close()}
              className="text-slate-600 border-slate-300"
            >
              Close
            </Button>
          </div>
        </div>
      </header>

      {/* Main Report Document Container */}
      <main className="max-w-4xl mx-auto p-6 md:p-10 print:p-0 print:max-w-full">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-8 md:p-12 print:shadow-none print:border-none print:p-6">
          
          {/* Header */}
          <div className="border-b-2 border-emerald-700 pb-6 mb-8 text-center relative">
            <div className="text-left mb-4 flex items-center justify-between">
              <div className="flex flex-col">
                <span className="font-extrabold text-2xl text-emerald-800 leading-tight">
                  MonitorHealth<span className="text-emerald-500">.AI</span>
                </span>
                <span className="text-xs text-slate-500 font-medium mt-0.5">AllyPsychiatry Clinical Network</span>
              </div>
              <div className="text-right text-xs text-slate-500">
                <p className="font-semibold text-slate-700">CONFIDENTIAL MEDICAL DOCUMENT</p>
                <p>HIPAA &amp; HITECH Compliant</p>
              </div>
            </div>

            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight mt-6 mb-2">
              PATIENT ENROLLMENT REPORT
            </h1>
            <p className="text-base text-slate-600 font-medium">
              Patient Healthcare Communication &amp; Behavioral Health Integration Services Consent Report
            </p>
            <div className="flex justify-center items-center gap-6 text-xs text-slate-500 mt-4 pt-3 border-t border-slate-100">
              <span><strong>Generated on:</strong> {registrationDate}</span>
              <span>&bull;</span>
              <span><strong>Practice:</strong> Ally Psychiatry</span>
              {patientId && (
                <>
                  <span>&bull;</span>
                  <span><strong>Patient ID:</strong> {patientId.slice(0, 8)}...</span>
                </>
              )}
            </div>
          </div>

          {/* Section: Patient Identification */}
          <section className="mb-8">
            <div className="flex items-center gap-2 border-b border-slate-200 pb-2 mb-4">
              <h2 className="text-base font-bold text-emerald-900 uppercase tracking-wider">
                Patient Identification
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 bg-slate-50 p-5 rounded-xl border border-slate-200 print:bg-transparent">
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block">Full Name</span>
                <span className="text-base font-bold text-slate-900">{patientName}</span>
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block">Date of Birth &amp; Gender</span>
                <span className="text-sm font-semibold text-slate-800">{dob} ({gender})</span>
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block">Mobile Phone</span>
                <span className="text-sm font-semibold text-slate-800">{mobile}</span>
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block">Email Address</span>
                <span className="text-sm font-semibold text-slate-800">{email}</span>
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block">Primary Provider</span>
                <span className="text-sm font-bold text-emerald-900">{primaryProvider}</span>
              </div>
              {secondaryProvider && (
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block">Secondary Provider</span>
                  <span className="text-sm font-semibold text-slate-800">{secondaryProvider}</span>
                </div>
              )}
              <div className="md:col-span-2">
                <span className="text-xs font-semibold text-slate-500 uppercase block">Encounter / Location</span>
                <span className="text-sm font-semibold text-slate-800">{location}</span>
              </div>
            </div>
          </section>

          {/* Section: Behavioral Health Integration Services & Consents Status */}
          <section className="mb-8">
            <div className="flex items-center gap-2 border-b border-slate-200 pb-2 mb-4">
              <h2 className="text-base font-bold text-emerald-900 uppercase tracking-wider">
                Behavioral Health Integration Services Enrollment &amp; Legal Consents Audit Log
              </h2>
            </div>

            <div className="border border-emerald-200 bg-emerald-50/60 rounded-xl p-5 mb-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-sm font-bold text-emerald-900">
                    Behavioral Health Integration Services (BHI) Participation Consent
                  </h3>
                  <p className="text-xs text-emerald-800 mt-1 leading-relaxed">
                    Patient has been informed of and consented to participate in Behavioral Health Integration Services for ongoing clinical evaluation in-between regular visits with their clinical care team.
                  </p>
                  <p className="text-xs font-semibold text-emerald-900 mt-2">
                    Acknowledged &amp; Timestamped: {acknowledgementDate}
                  </p>
                </div>
              </div>
            </div>

            <div className="border border-slate-200 bg-slate-50 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-slate-600 mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-sm font-bold text-slate-800">
                    HIPAA Clinical Communication &amp; Privacy Notice
                  </h3>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                    Patient acknowledged receipt of the Notice of Privacy Practices and consented to electronic communications regarding treatment plans, appointments, and therapeutic questionnaires.
                  </p>
                  <p className="text-xs font-semibold text-slate-700 mt-2">
                    Recorded Date: {registrationDate}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section: Audit Summary Table */}
          <section className="mb-8">
            <div className="flex items-center gap-2 border-b border-slate-200 pb-2 mb-4">
              <h2 className="text-base font-bold text-emerald-900 uppercase tracking-wider">
                Registration Audit Trail
              </h2>
            </div>

            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="bg-slate-100 text-slate-700 border-b border-slate-200">
                  <th className="py-2.5 px-3 font-semibold">Event</th>
                  <th className="py-2.5 px-3 font-semibold">Status</th>
                  <th className="py-2.5 px-3 font-semibold">Timestamp</th>
                  <th className="py-2.5 px-3 font-semibold">Attestation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="py-2.5 px-3 font-medium text-slate-800">New Patient Registration</td>
                  <td className="py-2.5 px-3 text-emerald-700 font-semibold">Completed</td>
                  <td className="py-2.5 px-3 text-slate-600">{registrationDate}</td>
                  <td className="py-2.5 px-3 text-slate-600">Electronic Intake Form</td>
                </tr>
                <tr>
                  <td className="py-2.5 px-3 font-medium text-slate-800">Behavioral Health Integration Services Consent</td>
                  <td className="py-2.5 px-3 text-emerald-700 font-semibold">Acknowledged</td>
                  <td className="py-2.5 px-3 text-slate-600">{acknowledgementDate}</td>
                  <td className="py-2.5 px-3 text-slate-600">Patient Attested</td>
                </tr>
                <tr>
                  <td className="py-2.5 px-3 font-medium text-slate-800">HIPAA Privacy Notice</td>
                  <td className="py-2.5 px-3 text-emerald-700 font-semibold">Accepted</td>
                  <td className="py-2.5 px-3 text-slate-600">{registrationDate}</td>
                  <td className="py-2.5 px-3 text-slate-600">Direct Submission</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Footer Notice */}
          <footer className="pt-6 border-t border-slate-200 text-center text-xs text-slate-500">
            <p className="font-semibold text-slate-600 uppercase tracking-wider mb-1">
              Confidentiality &amp; HIPAA Compliance Notice
            </p>
            <p className="leading-relaxed max-w-2xl mx-auto">
              This document contains Protected Health Information (PHI) intended solely for the authorized healthcare provider named above. Any unauthorized review, use, disclosure, or distribution is strictly prohibited under HIPAA and federal regulations.
            </p>
            <p className="mt-3 text-slate-400">
              &copy; {new Date().getFullYear()} MonitorHealth.AI &bull; AllyPsychiatry Care Network
            </p>
          </footer>

        </div>
      </main>

      {/* Print stylesheet */}
      <style>{`
        @media print {
          body {
            background-color: white !important;
            font-size: 11pt !important;
            color: black !important;
          }
          @page {
            size: A4 portrait;
            margin: 1.5cm;
          }
          header, button, .print\\:hidden {
            display: none !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:border-none {
            border: none !important;
          }
        }
      `}</style>
    </div>
  );
}
